/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { useEffect } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';

/**
 * GTM Script Loader — consent-gated GTM container loader.
 *
 * INT-21 spec: no `gtm.js` script may load into the DOM until `TrackingConsent === Accepted`.
 * Google's `gtm.js` is the surface that auto-fires built-in pageview/session tags, so it
 * must not be present pre-consent.
 *
 * The `window.dataLayer = window.dataLayer || []` bootstrap is rendered separately (as a
 * plain inline script in `Layout`'s `<head>`) so pre-consent `dataLayer.push` calls queue
 * safely — they simply pile up in the array until (or unless) GTM loads.
 *
 * This component lives *inside* the provider tree in `App` (needs `ConfigProvider` +
 * `AuthProvider` + React Router's `useFetcher` for `useTrackingConsent`). It attaches a
 * real `<script async>` DOM node via `document.createElement` — inline `<script>` nodes
 * inserted by React on the client after hydration do not execute.
 *
 * When `containerIdGA4` is set, a second `<script>` is attached so both GA4 and the
 * primary UA container load side-by-side.
 *
 * No `<noscript>` iframe: no-JS shoppers can't grant consent, so per spec we omit the
 * iframe until consent is Accepted — which by definition never happens without JS.
 */

interface GtmContainerParams {
    id: string;
    auth?: string;
    preview?: string;
    cookiesWin?: string;
}

function buildGtmUrl(params: GtmContainerParams): string {
    const q = new URLSearchParams();
    if (params.auth) q.set('gtm_auth', params.auth);
    if (params.preview) q.set('gtm_preview', params.preview);
    if (params.cookiesWin) q.set('gtm_cookies_win', params.cookiesWin);
    const query = q.toString();
    return `https://www.googletagmanager.com/gtm.js?id=${params.id}${query ? `&${query}` : ''}`;
}

function injectGtm(container: GtmContainerParams): HTMLScriptElement | null {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    const w = window as unknown as Record<string, unknown>;
    const dl = 'dataLayer';
    const queue = (w[dl] as unknown[] | undefined) ?? [];
    w[dl] = queue;
    (queue as Array<Record<string, unknown>>).push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });
    const first = document.getElementsByTagName('script')[0];
    if (!first?.parentNode) return null;
    const script = document.createElement('script');
    script.async = true;
    script.src = buildGtmUrl(container);
    script.setAttribute('data-gtm-container', container.id);
    first.parentNode.insertBefore(script, first);
    return script;
}

/**
 * Consent-gated GTM loader. Attaches gtm.js `<script async>` tag(s) once tracking consent
 * is Accepted. Must be rendered inside the provider tree (needs Config + Auth contexts).
 * Renders nothing itself — the script node is inserted directly into `<head>` (before the
 * first existing `<script>`) via the standard Google snippet pattern.
 */
export function GtmConsentGatedLoader() {
    const config = useConfig<AppConfig>();
    const { trackingConsent } = useTrackingConsent();
    const consented = trackingConsent === TrackingConsent.Accepted;

    const gtm = config.engagement?.gtm;
    const enabled = gtm?.enabled ?? false;
    const containerId = (enabled && gtm?.containerId?.trim()) || '';
    const containerIdGA4 = (enabled && gtm?.containerIdGA4?.trim()) || '';
    const auth = gtm?.auth;
    const preview = gtm?.preview;
    const cookiesWin = gtm?.cookiesWin;
    const authGA4 = gtm?.authGA4;
    const previewGA4 = gtm?.previewGA4;
    const cookiesWinGA4 = gtm?.cookiesWinGA4;

    useEffect(() => {
        if (!consented) return;
        const injected: HTMLScriptElement[] = [];
        if (containerId) {
            const node = injectGtm({ id: containerId, auth, preview, cookiesWin });
            if (node) injected.push(node);
        }
        if (containerIdGA4) {
            const node = injectGtm({
                id: containerIdGA4,
                auth: authGA4,
                preview: previewGA4,
                cookiesWin: cookiesWinGA4,
            });
            if (node) injected.push(node);
        }
        return () => {
            // On consent revocation / unmount, drop the script nodes we owned. The GTM
            // container itself may have already registered listeners on window — those
            // are outside our control until a full page reload — but removing the tag
            // stops the next page load from re-executing it.
            for (const node of injected) node.parentNode?.removeChild(node);
        };
    }, [consented, containerId, containerIdGA4, auth, preview, cookiesWin, authGA4, previewGA4, cookiesWinGA4]);

    return null;
}
